import { formatDate } from "../../../utils/date.util";
import { formatSeconds } from "../../../utils/number.util";
import {
  AccordionItem,
  AccordionButton,
  Flex,
  Spinner,
  AccordionIcon,
  AccordionPanel,
  Divider,
  Box,
} from "@chakra-ui/react";
import { FlexIconRow } from "../../shared/FlexIconRow";
import { StatusTag } from "../../shared/StatusTag";
import { IntegrationItem } from "../../../models/integration-item.model";

interface FocussedRun {
  id: string;
  status: any;
  logs?: any;
}
interface metric {
  totalDuration: number;
  averageDuration: number;
}

export const RunAttemptsHistoryCard = ({
  run,
  focussedRun,
  handleToggle,
  setRunHistory,
  metric,
}: {
  run: IntegrationItem;
  focussedRun: FocussedRun;
  handleToggle: (id: string) => void;
  setRunHistory: (id: string) => void;
  metric: metric;
}) => {
  const RUN_DEVIATION_THRESHOLD = 0.5;

  const runDurationAcceptable = (duration: number) => {
    const deviation = Math.abs(duration - metric.averageDuration);
    const deviationPercentage = deviation / metric.averageDuration;
    return deviationPercentage < RUN_DEVIATION_THRESHOLD;
  };

  return (
    <AccordionItem borderColor="gray.600">
      <AccordionButton
        onClick={() => handleToggle(run.id)}
        _hover={{ bg: "gray.800" }}
        py="12px"
      >
        <Box py="3px" as="span" flex="1" textAlign="left">
          <Flex flexDirection="row" justifyContent="space-between" w="80%">
            <FlexIconRow gap="18px">
              {!!run.conclusion ? (
                <RunDurationWarning
                  runDurationAcceptable={runDurationAcceptable}
                  run={run}
                />
              ) : (
                <RunStatus
                  focussedRun={focussedRun}
                  setRunHistory={setRunHistory}
                  run={run}
                />
              )}
            </FlexIconRow>
          </Flex>
        </Box>
        <AccordionIcon color="gray.400" />
      </AccordionButton>
      <AccordionPanel pb={4} color="gray.200">
        <br />
        {run.conclusion && (
          <RunDetails run={run} runDurationAcceptable={runDurationAcceptable} />
        )}

        <Divider my="12px" borderColor="gray.600" />

        {focussedRun.id === run.id && !!focussedRun?.logs?.length ? (
          <FocussedRunLogAvail
            focussedRun={focussedRun}
            setRunHistory={setRunHistory}
            run={run}
          />
        ) : (
          <FocussedRunLogNoAvail run={run} setRunHistory={setRunHistory} />
        )}
      </AccordionPanel>
    </AccordionItem>
  );
};

const RunDurationWarning = ({
  runDurationAcceptable,
  run,
}: {
  runDurationAcceptable: any;
  run: IntegrationItem;
}) => {
  return (
    <>
      {!runDurationAcceptable(run.duration) && <>⚠️</>}
      <StatusTag type={run.conclusion as any} style={{ width: "120px" }}>
        {run.conclusion}
      </StatusTag>
      <Box>{formatDate(run.lastRunDate)}</Box>
      <Box></Box>
    </>
  );
};

const RunStatus = ({
  focussedRun,
  setRunHistory,
  run,
}: {
  focussedRun: FocussedRun;
  setRunHistory: (id: string) => void;
  run: IntegrationItem;
}) => {
  return (
    <>
      <Spinner />
      <Box>
        {focussedRun?.status === "Completed" && focussedRun.id === run.id ? (
          "✅ Integration has completed. We are cleaning up now, but your Anaplan models should be updated."
        ) : (
          <Box
            cursor="pointer"
            _hover={{ textDecor: "underline" }}
            onClick={(e) => {
              e.stopPropagation();
              setRunHistory(run.id);
            }}
          >
            Currently runnning. Click here to refresh.
          </Box>
        )}
      </Box>
    </>
  );
};

const RunDetails = ({
  run,
  runDurationAcceptable,
}: {
  run: IntegrationItem;
  runDurationAcceptable: any;
}) => {
  return (
    <>
      <Flex flexDirection="column" gap="16px">
        <Box>
          Completed with status {run.conclusion} in {run.attempt} attempt(s).
        </Box>
        <Box>Duration: {formatSeconds(run.duration)}</Box>
        {!runDurationAcceptable(run.duration) && (
          <Box fontSize="13px" color="brand.700" letterSpacing="0.02em">
            Flagged — run duration deviates significantly from the average.
          </Box>
        )}
      </Flex>
    </>
  );
};

const FocussedRunLogAvail = ({
  focussedRun,
  setRunHistory,
  run,
}: {
  focussedRun: FocussedRun;
  setRunHistory: (id: string) => void;
  run: IntegrationItem;
}) => {
  return (
    <Flex
      flexDir="column"
      p="16px 20px"
      bg="brand.100"
      color="gray.100"
      borderRadius="2px"
      border="1px solid"
      borderColor="gray.600"
      fontFamily="mono"
      fontSize="12px"
      gap="6px"
    >
      {focussedRun.logs.map((log: any, i: number) => (
        <Flex key={i} flexDir="row" gap="12px">
          <Box color="gray.300">{log.Message?.S ?? "--"}</Box>
        </Flex>
      ))}
      {focussedRun.status === "Completed" ? (
        <Flex flexDir="row" justifyContent="center" color="brand.500" mt="8px" letterSpacing="0.08em" textTransform="uppercase" fontSize="10px">
          Integration completed
        </Flex>
      ) : (
        <Flex
          flexDir="row"
          justifyContent="center"
          color="brand.500"
          cursor="pointer"
          mt="8px"
          letterSpacing="0.08em"
          textTransform="uppercase"
          fontSize="10px"
          _hover={{ textDecor: "underline" }}
          onClick={() => setRunHistory(run.id)}
        >
          Load more
        </Flex>
      )}
    </Flex>
  );
};

const FocussedRunLogNoAvail = ({
  run,
  setRunHistory,
}: {
  run: IntegrationItem;
  setRunHistory: (id: string) => void;
}) => {
  return (
    <>
      {!!run.conclusion ? (
        "No logs are available for this integration run."
      ) : (
        <Box
          cursor="pointer"
          _hover={{ textDecor: "underline" }}
          onClick={() => setRunHistory(run.id)}
        >
          Currently running. Cick here to refresh.
        </Box>
      )}
    </>
  );
};
